import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogTrigger
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, 
  Plus,
  Loader2,
  ArrowLeft,
  Calendar,
  User,
  Pill,
  AlertTriangle,
  Heart,
  Activity,
  Stethoscope,
  Edit,
  Trash2,
  Download
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MedicalHistory = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [medicalData, setMedicalData] = useState({
    blood_type: '',
    allergies: [],
    chronic_conditions: [],
    current_medications: [],
    surgeries: [],
    family_history: [],
    emergency_contact: {
      name: '',
      phone: '',
      relationship: ''
    }
  });
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addType, setAddType] = useState('');
  const [newItem, setNewItem] = useState('');
  const [newItemDetails, setNewItemDetails] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchMedicalHistory();
  }, [token]);

  const fetchMedicalHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/users/medical-history`, { headers });
      if (response.data.medical_history) {
        setMedicalData(prev => ({ ...prev, ...response.data.medical_history }));
      }
    } catch (error) {
      console.error('Error fetching medical history:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveMedicalHistory = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/users/medical-history`,
        { medical_history: medicalData },
        { headers }
      );
      toast.success('Historique médical enregistré');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const addItem = (type) => {
    setAddType(type);
    setNewItem('');
    setNewItemDetails('');
    setShowAddDialog(true);
  };

  const confirmAddItem = () => {
    if (!newItem.trim()) return;
    
    const item = newItemDetails 
      ? { name: newItem, details: newItemDetails, date: new Date().toISOString() }
      : newItem;
    
    setMedicalData(prev => ({
      ...prev,
      [addType]: [...(prev[addType] || []), item]
    }));
    
    setShowAddDialog(false);
    toast.success('Élément ajouté');
  };

  const removeItem = (type, index) => {
    setMedicalData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
    toast.success('Élément supprimé');
  };

  const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  const getTypeLabel = (type) => {
    const labels = {
      allergies: 'Allergie',
      chronic_conditions: 'Condition chronique',
      current_medications: 'Médicament',
      surgeries: 'Chirurgie/Opération',
      family_history: 'Antécédent familial'
    };
    return labels[type] || type;
  };

  const exportMedicalHistory = () => {
    const dataStr = JSON.stringify(medicalData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historique_medical_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Historique médical exporté');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const ListSection = ({ title, icon: Icon, type, items, color }) => (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${color}`} />
            {title}
          </span>
          <Button variant="outline" size="sm" onClick={() => addItem(type)}>
            <Plus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {(!items || items.length === 0) ? (
          <p className="text-sm text-slate-500 italic">Aucun élément enregistré</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {items.map((item, index) => (
              <Badge 
                key={index} 
                variant="secondary" 
                className="px-3 py-1 flex items-center gap-2"
              >
                {typeof item === 'string' ? item : item.name}
                <button 
                  onClick={() => removeItem(type, index)}
                  className="hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="medical-history-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour au tableau de bord
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <FileText className="w-8 h-8 text-primary" />
                Mon Dossier Médical
              </h1>
              <p className="text-slate-500 mt-1">
                Informations confidentielles pour un meilleur suivi
              </p>
            </div>
            <Button variant="outline" onClick={exportMedicalHistory} className="gap-2">
              <Download className="w-4 h-4" />
              Exporter
            </Button>
          </div>
        </div>

        {/* Warning */}
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Information importante</p>
              <p className="text-amber-700">
                Ces informations sont confidentielles et partagées uniquement avec les professionnels 
                de santé que vous consultez. Elles aident à un meilleur diagnostic et traitement.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Blood Type & Emergency Contact */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Heart className="w-5 h-5 text-red-500" />
                Groupe sanguin
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {bloodTypes.map((type) => (
                  <Button
                    key={type}
                    variant={medicalData.blood_type === type ? "default" : "outline"}
                    size="sm"
                    onClick={() => setMedicalData(prev => ({ ...prev, blood_type: type }))}
                    className={medicalData.blood_type === type ? "bg-red-500 hover:bg-red-600" : ""}
                  >
                    {type}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5 text-blue-500" />
                Contact d'urgence
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input
                placeholder="Nom du contact"
                value={medicalData.emergency_contact?.name || ''}
                onChange={(e) => setMedicalData(prev => ({
                  ...prev,
                  emergency_contact: { ...prev.emergency_contact, name: e.target.value }
                }))}
              />
              <Input
                placeholder="Téléphone"
                value={medicalData.emergency_contact?.phone || ''}
                onChange={(e) => setMedicalData(prev => ({
                  ...prev,
                  emergency_contact: { ...prev.emergency_contact, phone: e.target.value }
                }))}
              />
              <Input
                placeholder="Relation (père, mère, conjoint...)"
                value={medicalData.emergency_contact?.relationship || ''}
                onChange={(e) => setMedicalData(prev => ({
                  ...prev,
                  emergency_contact: { ...prev.emergency_contact, relationship: e.target.value }
                }))}
              />
            </CardContent>
          </Card>
        </div>

        {/* Medical Lists */}
        <ListSection 
          title="Allergies" 
          icon={AlertTriangle} 
          type="allergies" 
          items={medicalData.allergies}
          color="text-orange-500"
        />

        <ListSection 
          title="Conditions chroniques" 
          icon={Activity} 
          type="chronic_conditions" 
          items={medicalData.chronic_conditions}
          color="text-purple-500"
        />

        <ListSection 
          title="Médicaments actuels" 
          icon={Pill} 
          type="current_medications" 
          items={medicalData.current_medications}
          color="text-green-500"
        />

        <ListSection 
          title="Chirurgies / Opérations" 
          icon={Stethoscope} 
          type="surgeries" 
          items={medicalData.surgeries}
          color="text-blue-500"
        />

        <ListSection 
          title="Antécédents familiaux" 
          icon={User} 
          type="family_history" 
          items={medicalData.family_history}
          color="text-slate-500"
        />

        {/* Save Button */}
        <div className="flex justify-end mt-6">
          <Button 
            onClick={saveMedicalHistory} 
            disabled={saving}
            className="gap-2"
            size="lg"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            Enregistrer mon dossier
          </Button>
        </div>

        {/* Add Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajouter {getTypeLabel(addType)}</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label>Nom / Description</Label>
                <Input
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  placeholder={`Nom de ${getTypeLabel(addType).toLowerCase()}`}
                />
              </div>
              {(addType === 'surgeries' || addType === 'chronic_conditions') && (
                <div>
                  <Label>Détails (optionnel)</Label>
                  <Textarea
                    value={newItemDetails}
                    onChange={(e) => setNewItemDetails(e.target.value)}
                    placeholder="Date, hôpital, remarques..."
                    rows={3}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Annuler
              </Button>
              <Button onClick={confirmAddItem} disabled={!newItem.trim()}>
                Ajouter
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MedicalHistory;
